import { ContentWorkflow, TechDomain } from '@amzn/awscat-pattern-library-service-model';
import {
  ColumnLayout,
  Container,
  FormField,
  Header,
  Icon,
  Multiselect,
  MultiselectProps,
  Select,
  SelectProps,
  SpaceBetween,
} from '@amzn/awsui-components-core';
import { useEffect, useState } from 'react';
import { TechDomainOptions } from '../utils/SelectableOptions';
import React from 'react';
import constants from '../../common/constants';

interface Props {
  workflow: ContentWorkflow;
  version: number;
  validateInput: (isInputValid: boolean) => void;
  updateSelectedTechDomains: (mainTech: TechDomain, techDomains?: TechDomain[]) => void;
}

const WorkflowActionUpdateMetadata = ({
  workflow,
  version,
  validateInput,
  updateSelectedTechDomains,
}: Props) => {
  /**
   * Get the main technology domain from the workflow
   * @returns the main tech domain
   */
  const getPrimaryDomainFromWorkflow = (): SelectProps.Option | undefined => {
    let mainTech: string = '';
    if (version === workflow?.draftWorkflowId) {
      mainTech = workflow.draftTemplateMetadata.mainTechDomain;
    } else {
      mainTech = workflow.publicationWorkflows.find((wf) => wf.workflowId === version)
        ?.templateMetadata?.mainTechDomain;
    }
    return TechDomainOptions.find((domain) => domain.value === mainTech);
  };

  /**
   * Get the additional technology domains from the workflow
   * @returns the additional tech domains, if any
   */
  const getSecondaryDomainsFromWorkflow = (excludeDisabled = false): MultiselectProps.Option[] => {
    let additionalTech: string[] = [];
    if (version === workflow?.draftWorkflowId) {
      additionalTech = workflow.draftTemplateMetadata.techDomains ?? [];
    } else {
      additionalTech =
        workflow.publicationWorkflows.find((wf) => wf.workflowId === version)?.templateMetadata
          ?.techDomains ?? [];
    }
    const domains = TechDomainOptions.filter(
      (domain) =>
        !!domain.value &&
        additionalTech.includes(domain.value) &&
        !(excludeDisabled && domain.disabled),
    );
    return domains;
  };

  const [selectedMainTech, setSelectedMainTech] = useState<SelectProps.Option | undefined>(
    getPrimaryDomainFromWorkflow(),
  );
  const [selectedAdditionalTech, setSelectedAdditionalTech] = useState<MultiselectProps.Option[]>(
    getSecondaryDomainsFromWorkflow(true),
  );
  const [mainTechErrorText, setMainTechErrorText] = useState<string>();
  const [additionalTechErrorText, setAdditionalTechErrorText] = useState<string>();

  useEffect(() => {
    let mainTechError: string | undefined;
    let additionalTechError: string | undefined;

    if (!selectedMainTech) {
      mainTechError = constants.REQUIRED;
    }
    if (
      selectedMainTech &&
      selectedAdditionalTech &&
      selectedAdditionalTech.includes(selectedMainTech)
    ) {
      additionalTechError = constants.TECH_DOMAINS_CONFLICTS_ERROR;
    }
    setMainTechErrorText(mainTechError);
    setAdditionalTechErrorText(additionalTechError);
    validateInput(!mainTechError && !additionalTechError);
    updateSelectedTechDomains(
      selectedMainTech?.value as TechDomain,
      selectedAdditionalTech?.map((item) => item.value as TechDomain),
    );
  }, [selectedMainTech, selectedAdditionalTech]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Reteives the additional technologies in the workflow and formats into a string to display to the user
   * @returns the list of secondary domain labels to display
   */
  const getSecondaryDomainLabels = (): string => {
    const domains = getSecondaryDomainsFromWorkflow();
    const domainLabels = domains
      .map((domain) => domain.label)
      .filter((label) => label !== undefined);
    return domainLabels.length ? domainLabels.join(', ') : '<None>';
  };

  const metadataForm = (
    <ColumnLayout columns={2}>
      <FormField
        label={constants.CONTENT_REQUEST_CREATION_MAIN_TECHNOLOGY_LABEL}
        description={constants.CONTENT_REQUEST_CREATION_MAIN_TECHNOLOGY_DESCRIPTION}
        errorText={mainTechErrorText}
        i18nStrings={{ errorIconAriaLabel: 'Error' }}
      >
        <Select
          selectedOption={selectedMainTech ?? null}
          options={TechDomainOptions}
          placeholder={`Choose ${constants.CONTENT_REQUEST_CREATION_MAIN_TECHNOLOGY_LABEL}`}
          onChange={({ detail }) => setSelectedMainTech(detail.selectedOption)}
          ariaRequired
        />
      </FormField>
      <FormField
        label={constants.CONTENT_REQUEST_CREATION_ADDITIONAL_TECHNOLOGY_LABEL}
        description={constants.CONTENT_REQUEST_CREATION_ADDITIONAL_TECHNOLOGY_DESCRIPTION}
        errorText={additionalTechErrorText}
        i18nStrings={{ errorIconAriaLabel: 'Error' }}
      >
        <Multiselect
          selectedOptions={selectedAdditionalTech}
          options={TechDomainOptions}
          placeholder={`Choose ${constants.CONTENT_REQUEST_CREATION_ADDITIONAL_TECHNOLOGY_LABEL}`}
          onChange={({ detail }) => setSelectedAdditionalTech([...detail.selectedOptions])}
          keepOpen
          deselectAriaLabel={() => 'Remove technology'}
          ariaRequired
        />
      </FormField>
    </ColumnLayout>
  );

  const metadataChangeSummary = (
    <Container header={<Header variant="h3">Summary of changes</Header>}>
      <SpaceBetween size="s">
        <SpaceBetween size="xs" direction="horizontal">
          {`Primary: ${getPrimaryDomainFromWorkflow()?.label}`}
          <Icon name="arrow-right" />
          {`${selectedMainTech?.label ?? '<None>'}`}
        </SpaceBetween>
        <SpaceBetween size="xs" direction="horizontal">
          {`Secondary: ${getSecondaryDomainLabels()}`}
          <Icon name="arrow-right" />
          {`${selectedAdditionalTech.length ? selectedAdditionalTech?.map((domain) => domain.label).join(', ') : '<None>'}`}
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );

  return (
    <SpaceBetween size="m">
      {metadataForm}
      {metadataChangeSummary}
    </SpaceBetween>
  );
};

export default WorkflowActionUpdateMetadata;
